import { useState } from 'react';
import {
  Box, Flex, Heading, Text, Badge, Button, ButtonGroup,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  useColorModeValue,
} from '@chakra-ui/react';
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import Card from '../../components/card/Card';
import DataTable from '../../components/table/DataTable';
import TableSearch from '../../components/table/TableSearch';

interface LogEntry {
  timestamp: string;
  severity: string;
  message: string;
  component: string;
  traceId: string | null;
}

interface LogPage {
  items: LogEntry[];
  totalCount: number;
}

const columnHelper = createColumnHelper<LogEntry>();

function severityBadgeColor(severity: string): string {
  switch (severity) {
    case 'Error':
    case 'Critical':
      return 'red';
    case 'Warning':
      return 'orange';
    case 'Information':
      return 'blue';
    case 'Debug':
    case 'Trace':
    default:
      return 'gray';
  }
}

export function LogViewerPage() {
  const { t } = useTranslation();
  const [timeRange, setTimeRange] = useState('1h');
  const [severity, setSeverity] = useState('');
  const [component, setComponent] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [selectedEntry, setSelectedEntry] = useState<LogEntry | null>(null);

  const textColor = useColorModeValue('navy.700', 'white');
  const secondaryTextColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');
  const modalBg = useColorModeValue('gray.50', 'navy.900');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin-logs', timeRange, severity, component, search, page, pageSize],
    queryFn: () => apiClient.get<LogPage>('/admin/logs', {
      timeRange,
      page: page + 1,
      pageSize,
      ...(severity && { severity }),
      ...(component && { component }),
      ...(search && { search }),
    }),
  });

  const severities = ['Trace', 'Debug', 'Information', 'Warning', 'Error', 'Critical'];
  const timeRanges = ['15m', '1h', '6h', '24h', '7d'];

  const columns = [
    columnHelper.accessor('timestamp', {
      header: t('admin.logs.timestamp'),
      cell: (info) => (
        <Text fontSize="sm" color={textColor}>
          {new Date(info.getValue()).toLocaleString()}
        </Text>
      ),
    }),
    columnHelper.accessor('severity', {
      header: t('admin.logs.severity'),
      cell: (info) => (
        <Badge colorScheme={severityBadgeColor(info.getValue())} borderRadius="8px" px="10px">
          {info.getValue()}
        </Badge>
      ),
    }),
    columnHelper.accessor('component', {
      header: t('admin.logs.component'),
      cell: (info) => (
        <Text fontSize="sm" color={secondaryTextColor}>
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('message', {
      header: t('admin.logs.message'),
      cell: (info) => (
        <Text fontSize="sm" color={textColor} noOfLines={1} maxW="400px">
          {info.getValue()}
        </Text>
      ),
    }),
  ];

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="log-viewer-page">
      <Card p="20px" mb="20px">
        <Flex justify="space-between" align="center" mb="20px">
          <Heading as="h1" size="lg" color={textColor}>
            {t('admin.logs.title')}
          </Heading>
          <Button
            size="sm"
            variant="brand"
            onClick={() => refetch()}
            data-testid="btn-refresh"
          >
            {t('admin.common.refresh')}
          </Button>
        </Flex>

        <Flex mb="16px" gap="8px">
          <ButtonGroup size="sm" isAttached variant="outline">
            {timeRanges.map((tr) => (
              <Button
                key={tr}
                variant={timeRange === tr ? 'brand' : 'outline'}
                onClick={() => { setTimeRange(tr); setPage(0); }}
                data-testid={`btn-range-${tr}`}
              >
                {tr}
              </Button>
            ))}
          </ButtonGroup>
        </Flex>

        <TableSearch
          searchValue={search}
          onSearchChange={(value) => { setSearch(value); setPage(0); }}
          searchPlaceholder={t('common.search')}
          searchTestId="search-input"
          filters={[
            {
              key: 'severity',
              label: t('admin.logs.severity'),
              options: [
                { value: '', label: t('common.all', 'All') },
                ...severities.map((s) => ({ value: s, label: s })),
              ],
              value: severity,
              onChange: (value) => { setSeverity(value); setPage(0); },
              testId: 'filter-severity',
            },
            {
              key: 'component',
              label: t('admin.logs.component'),
              options: [
                { value: '', label: t('common.all', 'All') },
              ],
              value: component,
              onChange: (value) => { setComponent(value); setPage(0); },
              testId: 'filter-component',
            },
          ]}
        />
      </Card>

      <DataTable<LogEntry>
        data={data?.items ?? []}
        columns={columns as ColumnDef<LogEntry>[]}
        totalCount={data?.totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
        isLoading={isLoading}
        onRowClick={setSelectedEntry}
        emptyText={t('admin.common.noResults')}
        data-testid="log-entries"
      />

      {/* Log Detail Modal */}
      <Modal isOpen={!!selectedEntry} onClose={() => setSelectedEntry(null)} size="xl">
        <ModalOverlay />
        <ModalContent data-testid="log-detail-modal">
          <ModalHeader color={textColor}>{t('admin.logs.details')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedEntry && (
              <Flex direction="column" gap={3}>
                <Flex gap={4}>
                  <Box>
                    <Text color={secondaryTextColor}>{t('admin.logs.timestamp')}</Text>
                    <Text color={textColor}>{new Date(selectedEntry.timestamp).toLocaleString()}</Text>
                  </Box>
                  <Box>
                    <Text color={secondaryTextColor}>{t('admin.logs.severity')}</Text>
                    <Badge colorScheme={severityBadgeColor(selectedEntry.severity)} borderRadius="8px" px="10px">
                      {selectedEntry.severity}
                    </Badge>
                  </Box>
                  <Box>
                    <Text color={secondaryTextColor}>{t('admin.logs.component')}</Text>
                    <Text color={textColor}>{selectedEntry.component}</Text>
                  </Box>
                </Flex>
                {selectedEntry.traceId && (
                  <Box>
                    <Text color={secondaryTextColor}>Trace ID</Text>
                    <Text color={textColor}>{selectedEntry.traceId}</Text>
                  </Box>
                )}
                <Box p={3} bg={modalBg} borderRadius="md" overflowX="auto">
                  <Text fontFamily="mono" whiteSpace="pre-wrap" color={textColor}>
                    {selectedEntry.message}
                  </Text>
                </Box>
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
